import { useCallback, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { cloneDeep } from 'lodash';

export type VersionSource = 'agent' | 'manual';

export interface IQuizVersion {
  snapshot: any;
  source: VersionSource;
  label: string;
}

// Kept small on purpose — snapshots are the whole form-shape quiz (cheap,
// images are URLs not blobs), but there's no reason to hold more than a
// handful of undo steps in memory for the lifetime of one dialog session.
const MAX_VERSIONS = 5;

interface IHistoryState {
  versions: IQuizVersion[];
  currentIndex: number;
}

/**
 * Linear undo/redo stack over the single unified RHF form that backs
 * QuizAgentDialog. One entry = one committed change, whichever of the two
 * things that can change the quiz caused it:
 *  - a fully-settled agent reply (pushed once the stream completes, not per
 *    chunk — see the `loading` watcher in index.tsx)
 *  - a manual edit committed via a nested dialog's Apply, or an instant
 *    action like add/delete page
 *
 * Pushing while `currentIndex` isn't at the tail (i.e. after an Undo)
 * truncates the redo tail first, matching standard undo/redo semantics.
 * Resets entirely when the dialog unmounts — there's no persistence.
 */
export const useVersionHistory = (methods: UseFormReturn<any>) => {
  const [{ versions, currentIndex }, setState] = useState<IHistoryState>({
    versions: [],
    currentIndex: -1,
  });
  const [locked, setLocked] = useState(false);

  const pushVersion = useCallback((label: string, source: VersionSource) => {
    setState((state) => {
      const truncated = state.versions.slice(0, state.currentIndex + 1);
      const next = [...truncated, { snapshot: cloneDeep(methods.getValues()), source, label }];
      const overflow = next.length - MAX_VERSIONS;
      const trimmed = overflow > 0 ? next.slice(overflow) : next;
      return { versions: trimmed, currentIndex: trimmed.length - 1 };
    });
  }, [methods]);

  // methods.reset(...) is an imperative side effect, so it must not run
  // inside the setState updater (updaters are expected to be pure and can
  // be invoked more than once under concurrent rendering). It runs here
  // instead, against the current render's `versions`/`currentIndex`, and
  // the updater below only ever does the pure index bookkeeping.
  const undo = useCallback(() => {
    if (currentIndex <= 0) return;
    const nextIndex = currentIndex - 1;
    methods.reset(cloneDeep(versions[nextIndex].snapshot), { keepDirty: false, keepTouched: true });
    setState((state) => ({ ...state, currentIndex: nextIndex }));
  }, [methods, versions, currentIndex]);

  const redo = useCallback(() => {
    if (currentIndex >= versions.length - 1) return;
    const nextIndex = currentIndex + 1;
    methods.reset(cloneDeep(versions[nextIndex].snapshot), { keepDirty: false, keepTouched: true });
    setState((state) => ({ ...state, currentIndex: nextIndex }));
  }, [methods, versions, currentIndex]);

  const lock = useCallback(() => setLocked(true), []);
  const unlock = useCallback(() => setLocked(false), []);

  return {
    pushVersion,
    undo,
    redo,
    canUndo: !locked && currentIndex > 0,
    canRedo: !locked && currentIndex < versions.length - 1,
    total: versions.length,
    position: currentIndex + 1,
    currentLabel: versions[currentIndex]?.label,
    lock,
    unlock,
  };
};

export type IVersionHistory = ReturnType<typeof useVersionHistory>;
